import { useQuery } from '@apollo/client';
import type {
  FetchFlagKpIsQuery,
  FetchFlagKpIsQueryVariables,
} from '@generated/graphql';
import { useMemo } from 'react';
import { FETCH_FLAG_KPIS } from '../utils/flags.queries';
import {
  ageCutoffIso,
  SPOTLIGHT_TYPES,
  UNRESOLVED_TILE_AGE_DAYS,
} from '../utils/reconFlags.config';

export const useReconFlagsKpiCounts = (
  organizationId?: string,
): {
  kpiCounts: Record<string, number>;
  orgTotalFlags: number;
  unresolvedThreePlusCount: number;
  refetch: () => Promise<unknown>;
} => {
  const { data: kpiQueryData, refetch } = useQuery<
    FetchFlagKpIsQuery,
    FetchFlagKpIsQueryVariables
  >(FETCH_FLAG_KPIS, {
    skip: !organizationId,
    variables: {
      organizationId: organizationId ?? '',
      // Fixed threshold, independent of the Age filter selection.
      unresolvedBefore: ageCutoffIso(UNRESOLVED_TILE_AGE_DAYS),
    },
    fetchPolicy: 'cache-and-network',
  });
  const kpis = kpiQueryData?.fetchFlagKPIs;

  const kpiCounts = useMemo(() => {
    const byType = new Map(
      (kpis?.typeCounts ?? []).map((row) => [row.flagType, row.count]),
    );
    return Object.fromEntries(
      SPOTLIGHT_TYPES.map(({ type }) => [type, byType.get(type) ?? 0]),
    ) as Record<string, number>;
  }, [kpis?.typeCounts]);

  const orgTotalFlags = kpis?.totalFlags ?? 0;
  const unresolvedThreePlusCount = kpis?.unresolvedOverThreeDays ?? 0;

  return { kpiCounts, orgTotalFlags, unresolvedThreePlusCount, refetch };
};
